import { PDFDocument } from "pdf-lib";

/** Kader op een blad, genormaliseerd 0–1 t.o.v. het blad zoals getoond (linksboven = 0,0). */
export interface Zone {
  name: string;
  level: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Rechthoek in PDF-gebruikerscoördinaten (punten, oorsprong linksonder). */
export interface PdfRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Het deel van een pdf.js-viewport dat nodig is om schermcoördinaten om te rekenen. */
export interface ViewportLike {
  width: number;
  height: number;
  convertToPdfPoint(x: number, y: number): number[];
}

/** Kleiner dan dit (deel van breedte of hoogte) telt niet als kader. */
export const MIN_ZONE = 0.02;

/** Zone (zoals getoond, eventueel gedraaid) → rechthoek in PDF-punten; werkt ook bij /Rotate en een eigen CropBox. */
export function zoneToPdfRect(z: Pick<Zone, "x" | "y" | "w" | "h">, vp: ViewportLike): PdfRect {
  const [x1, y1] = vp.convertToPdfPoint(z.x * vp.width, z.y * vp.height) as [number, number];
  const [x2, y2] = vp.convertToPdfPoint((z.x + z.w) * vp.width, (z.y + z.h) * vp.height) as [number, number];
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
}

/**
 * Nieuwe PDF met één blad per zone: kopie van het bronblad met MediaBox/CropBox op de zone.
 * De inhoud van het blad wordt gedeeld (pdf-lib kopieert gerefereerde objecten één keer), /Rotate blijft.
 */
export async function cropPdfToZones(src: Uint8Array, pageIndex: number, rects: PdfRect[]): Promise<Uint8Array> {
  const source = await PDFDocument.load(src, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const pages = await out.copyPages(
    source,
    rects.map(() => pageIndex),
  );
  pages.forEach((p, i) => {
    const r = rects[i]!;
    p.setMediaBox(r.x, r.y, r.w, r.h);
    p.setCropBox(r.x, r.y, r.w, r.h);
    out.addPage(p);
  });
  return out.save({ useObjectStreams: false });
}

/**
 * Afbeelding → PDF met één blad per zone (1 px = 1 pt). De afbeelding wordt één keer ingesloten
 * en per blad verschoven getekend; alles buiten het blad valt weg.
 */
export async function cropImageToZones(
  image: { bytes: Uint8Array; mime: "image/png" | "image/jpeg"; width: number; height: number },
  zones: Pick<Zone, "x" | "y" | "w" | "h">[],
): Promise<Uint8Array> {
  const out = await PDFDocument.create();
  const img = image.mime === "image/png" ? await out.embedPng(image.bytes) : await out.embedJpg(image.bytes);
  const W = image.width;
  const H = image.height;
  for (const z of zones) {
    const pw = z.w * W;
    const ph = z.h * H;
    const page = out.addPage([pw, ph]);
    // Linksboven van de zone moet linksboven op het blad komen (PDF: oorsprong linksonder).
    page.drawImage(img, { x: -z.x * W, y: ph + z.y * H - H, width: W, height: H });
  }
  return out.save({ useObjectStreams: false });
}

/** Normaliseren van een getrokken kader (ook als van rechtsonder naar linksboven getrokken), binnen 0–1. */
export function normRect(a: { x: number; y: number }, b: { x: number; y: number }) {
  const c = (v: number) => Math.min(1, Math.max(0, v));
  const x = c(Math.min(a.x, b.x));
  const y = c(Math.min(a.y, b.y));
  return { x, y, w: c(Math.max(a.x, b.x)) - x, h: c(Math.max(a.y, b.y)) - y };
}
